import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles, Divider } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { get, compact } from 'lodash';
import { useIntl } from 'react-intl';
import { Money2 } from '~/modules/common/components';
import { ExpenseEntryType } from '~/types';
import { useHasFeatureFlag } from '~/modules/common/hooks';

const useStyles = makeStyles(theme => ({
  actuals: {
    fontSize: theme.typography.body2.fontSize,
    height: 'auto',
    width: theme.spacing(16.5),
    paddingLeft: theme.spacing(0.5),
    '& input': {
      fontSize: 'small'
    },
    borderLeft: `1px solid ${theme.palette.divider}`
  },
  estimates: {
    fontSize: theme.typography.body2.fontSize,
    height: 'auto',
    paddingRight: theme.spacing(0.5),
    '& input': {
      fontSize: 'medium'
    }
  },
  estimatesWithActuals: {
    fontSize: theme.typography.body2.fontSize,
    height: 'auto',
    width: theme.spacing(16.5),
    paddingRight: theme.spacing(0.5),
    '& input': {
      fontSize: 'small'
    }
  },
  currency: {
    fontSize: theme.typography.body2.fontSize,
    '& div': {
      paddingRight: 'unset !important'
    }
  },
  divider: {
    height: 'auto',
    color: theme.palette.grey[100]
  },
  row: {
    display: 'flex'
  }
}));

const totalEstimatesField = 'totalEstimates';

const keys = {
  actualBillableAmount: 'actualBillableAmount',
  estimatedBillableAmount: 'estimatedBillableAmount',
  actualNonBillableAmount: 'actualNonBillableAmount',
  estimatedNonBillableAmount: 'estimatedNonBillableAmount'
};

const editableDataPoints = [
  keys.estimatedBillableAmount,
  keys.estimatedNonBillableAmount
];

const getAmountPath = (field, path) => `${field}.${path}`;

const roundToCurrency = val => Math.round(val * 100) / 100;

const updateAmountValue = (
  setFieldValue,
  index,
  isPsaPrpExpenseAmountChangeInLoopFixEnabled
) => (field, path, val) =>
  isPsaPrpExpenseAmountChangeInLoopFixEnabled
    ? setFieldValue(
        `allowedExpensesAndEstimates[${index}].${field}.${path}`,
        roundToCurrency(val)
      )
    : setFieldValue(
        `allowedExpensesAndEstimates[${index}].${field}.${path}`,
        val
      );

export const ExpenseAmount = ({
  column: { disabled, projectPeriodKeys },
  editable,
  field,
  index,
  record
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const {
    values: { showActuals, currency },
    setFieldValue
  } = useFormikContext();

  const isUpdatingRef = useRef(false);

  const getAmount = useCallback(
    (path, defaultValue = 0) =>
      get(record, getAmountPath(field, path), defaultValue),
    [field, record]
  );

  const isPsaPrpExpenseAmountChangeInLoopFixEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPrpExpenseAmountChangeInLoopFixEnabled'
  });

  const updateAmount = useMemo(
    () =>
      updateAmountValue(
        setFieldValue,
        index,
        isPsaPrpExpenseAmountChangeInLoopFixEnabled
      ),
    [index, setFieldValue, isPsaPrpExpenseAmountChangeInLoopFixEnabled]
  );

  const handleTotalFieldUpdate = useCallback(
    (path, val) => {
      const periodLength = projectPeriodKeys.length;
      const amountPerPeriod = roundToCurrency(val / periodLength);

      for (let i = 0; i < periodLength - 1; i++) {
        updateAmount(projectPeriodKeys[i], path, amountPerPeriod);
      }

      const distributedTotal = amountPerPeriod * (periodLength - 1);
      const lastPeriodAmount = roundToCurrency(val - distributedTotal);

      updateAmount(projectPeriodKeys[periodLength - 1], path, lastPeriodAmount);
    },
    [projectPeriodKeys, updateAmount]
  );

  const handlePeriodFieldUpdate = useCallback(
    (path, val, prevAmount) => {
      const currentTotal = get(
        record,
        getAmountPath(totalEstimatesField, path),
        0
      );

      updateAmount(totalEstimatesField, path, currentTotal - prevAmount + val);
    },
    [record, updateAmount]
  );

  const onAmountChange = useCallback(
    path => e => {
      const val = e.target.value || 0;
      const prevAmount = getAmount(path);

      if (!isNaN(val) && val !== prevAmount) {
        updateAmount(field, path, val);

        if (field === totalEstimatesField) {
          const periodLength = projectPeriodKeys.length;

          projectPeriodKeys.forEach(f => {
            updateAmount(f, path, val / periodLength);
          });
        } else {
          const currentTotal = get(
            record,
            getAmountPath(totalEstimatesField, path),
            0
          );

          updateAmount(
            totalEstimatesField,
            path,
            currentTotal - prevAmount + val
          );
        }
      }
    },
    [field, getAmount, projectPeriodKeys, record, updateAmount]
  );

  const onAmountChange2 = useCallback(
    path => e => {
      if (isUpdatingRef.current) {
        return;
      }

      const val = e.target.value || 0;
      const prevAmount = getAmount(path);

      const hasChanged = !isNaN(val) && Math.abs(val - prevAmount) > 0;

      if (!hasChanged) {
        return;
      }

      isUpdatingRef.current = true;

      try {
        updateAmount(field, path, val);

        if (field === totalEstimatesField) {
          handleTotalFieldUpdate(path, val);
        } else {
          handlePeriodFieldUpdate(path, val, prevAmount);
        }
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [
      field,
      getAmount,
      updateAmount,
      handleTotalFieldUpdate,
      handlePeriodFieldUpdate
    ]
  );

  const currencyClasses = useMemo(() => ({ currency: classes.currency }), [
    classes.currency
  ]);

  const getExpenseDataPoints = useCallback(
    (estimatedKey, actualKey) => {
      return compact([
        {
          key: estimatedKey,
          className: showActuals
            ? classes.estimatesWithActuals
            : classes.estimates
        },
        showActuals && {
          key: actualKey,
          className: classes.actuals,
          divider: (
            <Divider className={classes.divider} orientation="vertical" />
          )
        }
      ]);
    },
    [
      classes.actuals,
      classes.divider,
      classes.estimates,
      classes.estimatesWithActuals,
      showActuals
    ]
  );

  const expenseCategories = useMemo(
    () =>
      compact([
        record.billableType !== ExpenseEntryType.NonBillable && {
          key: 'billable',
          dataPoints: getExpenseDataPoints(
            keys.estimatedBillableAmount,
            keys.actualBillableAmount
          )
        },
        record.billableType !== ExpenseEntryType.Billable && {
          key: 'nonBillable',
          dataPoints: getExpenseDataPoints(
            keys.estimatedNonBillableAmount,
            keys.actualNonBillableAmount
          )
        }
      ]),
    [getExpenseDataPoints, record.billableType]
  );

  return (
    <Grid container spacing={0}>
      {expenseCategories.map(({ key, dataPoints }) => (
        <Grid key={key} item xs={12}>
          <div className={classes.row}>
            {dataPoints.map(({ key: dataPointKey, className }) => (
              <React.Fragment key={dataPointKey}>
                <Money2
                  id={
                    isPsaPrpExpenseAmountChangeInLoopFixEnabled
                      ? `${field}${key}${dataPointKey}`
                      : undefined
                  }
                  classes={currencyClasses}
                  className={className}
                  variant="standard"
                  amount={getAmount(dataPointKey)}
                  currency={currency}
                  onAmountChange={
                    isPsaPrpExpenseAmountChangeInLoopFixEnabled
                      ? onAmountChange2(dataPointKey)
                      : onAmountChange(dataPointKey)
                  }
                  currencyEditable={false}
                  editable={
                    editable &&
                    editableDataPoints.includes(dataPointKey) &&
                    !disabled
                  }
                  ariaLabel={intl.formatMessage({
                    id: `expenseBillPlan.${dataPointKey}`
                  })}
                />
              </React.Fragment>
            ))}
          </div>
        </Grid>
      ))}
    </Grid>
  );
};

ExpenseAmount.propTypes = {
  column: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  field: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  record: PropTypes.object.isRequired
};

export const ExpenseAmountEditor = props => (
  <ExpenseAmount {...props} editable />
);

export const ExpenseAmountFormatter = props => (
  <ExpenseAmount {...props} editable={false} />
);
